"use client"

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Handle, Position, useNodes, useEdges } from "reactflow"
import { router } from "@inertiajs/react"
import { MessageSquare, Minus, Plus, Trash2, AlertTriangle } from "lucide-react"
import "./Node.css"
import "./TextNode.css"
import { useWorkflow } from "@/lib/contexts/workflow-context"
import CopyPasteButtons from "./CopyPasteButtons"
import EnhancedVariableReference from "./EnhancedVariableReference"
import { collectUpstreamFormFieldVariables, hasUpstreamTaskNode } from "@/lib/workflowFormFieldVariables"
import { getSystemVariablesForContext } from "@/lib/workflowSystemVariables"
import {
  renderPreviewContent,
  estimateSmsSegments,
  suggestVariableMapping,
  TEXT_TEMPLATE_CATEGORIES,
} from "@/lib/textTemplateVariables"

const ensureString = (value) => (value === null || value === undefined ? "" : String(value))

const getSettingValue = (source, key, fallback = "") => {
  if (source?.[key] !== undefined && source?.[key] !== null) {
    return ensureString(source[key])
  }
  return ensureString(fallback)
}

const VAR_HINT = "Tip: use {{variableName}} to pull values from previous steps."

const TextNode = ({ data, id, csrf_token, textTemplates = [], twilio = {} }) => {
  const { currentWorkflow, updateNode } = useWorkflow()
  const { expanded = true } = data
  const settings = data?.settings || {}

  const [isHovering, setIsHovering] = useState(false)
  const [showMinimizeTooltip, setShowMinimizeTooltip] = useState(false)

  const nodes = useNodes()
  const edges = useEdges()

  const buildState = (src, srcSettings) => ({
    label: ensureString(src.label) || "Send SMS",
    name: ensureString(src.name) || ensureString(src.label) || "Send SMS",
    description: ensureString(src.description),
    templateId: ensureString(src.templateId ?? srcSettings?.template_id ?? ""),
    recipientSource:
      ensureString(src.recipientSource) ||
      getSettingValue(srcSettings, "recipient_source", "variable") ||
      "variable",
    recipientPhone:
      ensureString(src.recipientPhone) || getSettingValue(srcSettings, "recipient_phone", ""),
    variableMappings: src.variableMappings || srcSettings?.variable_mappings || {},
    messageOverride:
      ensureString(src.messageOverride) || getSettingValue(srcSettings, "message_override", ""),
    useTemplateMessage:
      typeof src.useTemplateMessage === "boolean"
        ? src.useTemplateMessage
        : (srcSettings?.use_template_message ?? true),
    mediaUrls: src.mediaUrls || srcSettings?.media_urls || [],
    mediaSource:
      ensureString(src.mediaSource) ||
      getSettingValue(srcSettings, "media_source", "both") ||
      "both",
    expanded: src.expanded ?? true,
  })

  const [nodeData, setNodeData] = useState(() => buildState(data, settings))
  const nodeDataRef = useRef(nodeData)

  useEffect(() => {
    setNodeData(buildState(data, data?.settings || {}))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data.label,
    data.name,
    data.description,
    data.templateId,
    data.recipientSource,
    data.recipientPhone,
    data.variableMappings,
    data.messageOverride,
    data.useTemplateMessage,
    data.mediaUrls,
    data.mediaSource,
    data.expanded,
    data.settings,
    id,
  ])

  useEffect(() => {
    nodeDataRef.current = nodeData
  }, [nodeData])

  // Resolve the persisted database id for this step. Once we have it the node
  // is "live" and update/delete via Inertia is enabled.
  const stepDatabaseId =
    data?.databaseId ??
    (typeof id === "string" && id.startsWith("step-") ? id.replace("step-", "") : null)

  const selectedTemplate = useMemo(
    () => textTemplates.find((t) => String(t.id) === String(nodeData.templateId)),
    [textTemplates, nodeData.templateId],
  )

  // ---------- Upstream variable discovery (matches CreateNoteNode pattern) -----
  const getAvailableRequestVariables = useCallback(() => {
    if (!nodes || !edges) return []
    const availableVariables = []
    const visitedNodes = new Set()

    const findUpstreamRequestNodes = (nodeId) => {
      if (visitedNodes.has(nodeId)) return
      visitedNodes.add(nodeId)

      const node = nodes.find((n) => n.id === nodeId)
      if (!node) return

      if (
        (node.type === "requestNode" || node.type === "getNoteNode") &&
        Array.isArray(node.data?.extractedVariables)
      ) {
        availableVariables.push(...node.data.extractedVariables)
      }

      const incomingEdges = edges.filter((edge) => edge.target === nodeId)
      incomingEdges.forEach((edge) => findUpstreamRequestNodes(edge.source))
    }

    findUpstreamRequestNodes(id)
    return availableVariables.filter((v) => v && typeof v.name === "string")
  }, [nodes, edges, id])

  const getAvailableFormFields = useCallback(
    () => collectUpstreamFormFieldVariables(nodes, edges, id),
    [nodes, edges, id],
  )

  const systemVariables = useMemo(
    () => getSystemVariablesForContext({
      hasUpstreamCompletedTask: hasUpstreamTaskNode(nodes, edges, id),
    }),
    [nodes, edges, id],
  )

  const getAvailableVariables = useCallback(
    () => [...getAvailableRequestVariables(), ...getAvailableFormFields()],
    [getAvailableRequestVariables, getAvailableFormFields],
  )

  const upstreamVariables = useMemo(() => getAvailableVariables(), [getAvailableVariables])

  // ---------- Template variable rendering ----------
  const templateVariables = useMemo(() => {
    if (!selectedTemplate?.variables) return []
    return selectedTemplate.variables.map((v) => (typeof v === "string" ? { key: v } : v))
  }, [selectedTemplate])

  const renderedMessage = useMemo(() => {
    if (!nodeData.useTemplateMessage && nodeData.messageOverride) {
      return nodeData.messageOverride
    }
    if (!selectedTemplate?.content) return nodeData.messageOverride || ""

    let content = selectedTemplate.content
    Object.entries(nodeData.variableMappings || {}).forEach(([slot, mappingValue]) => {
      const token = new RegExp(
        `\\{\\{\\s*${slot.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*\\}\\}`,
        "g",
      )
      content = content.replace(token, mappingValue || "")
    })

    return renderPreviewContent(content)
  }, [nodeData, selectedTemplate])

  const segmentCount = estimateSmsSegments(renderedMessage)

  // ---------- Persistence helpers (mirror CreateNoteNode) ----------
  const updateWorkflowContext = useCallback(
    (updatedData) => {
      if (currentWorkflow) {
        updateNode(currentWorkflow.id, id, { data: updatedData })
      }
    },
    [currentWorkflow, id, updateNode],
  )

  const persistData = useCallback(
    (updated) => {
      updateWorkflowContext(updated)
      if (data.onNodeDataChange) {
        data.onNodeDataChange(id, { ...updated, expanded })
      }
    },
    [updateWorkflowContext, data, id, expanded],
  )

  const handleChange = (field, value, options = {}) => {
    const { persistOnChange = false } = options
    const updated = {
      ...nodeDataRef.current,
      [field]: typeof value === "boolean" ? value : ensureString(value),
    }
    if (field === "name") {
      updated.label = ensureString(value) || "Send SMS"
    }
    setNodeData(updated)
    if (persistOnChange) {
      persistData(updated)
    }
  }

  const handleTemplateChange = (templateId) => {
    const template = textTemplates.find((t) => String(t.id) === String(templateId))
    const mappings = { ...(nodeDataRef.current.variableMappings || {}) }

    if (template?.variables) {
      template.variables.forEach((variable) => {
        const key = typeof variable === "string" ? variable : variable.key
        if (!mappings[key]) {
          const suggestion = suggestVariableMapping(key, upstreamVariables)
          if (suggestion) mappings[key] = suggestion
        }
      })
    }

    const updated = {
      ...nodeDataRef.current,
      templateId: ensureString(templateId),
      variableMappings: mappings,
      useTemplateMessage: true,
      messageOverride: "",
    }
    setNodeData(updated)
    persistData(updated)
  }

  const handleMappingChange = (slot, value) => {
    const updated = {
      ...nodeDataRef.current,
      variableMappings: {
        ...(nodeDataRef.current.variableMappings || {}),
        [slot]: value,
      },
    }
    setNodeData(updated)
  }

  // Persist on blur (matches CreateNoteNode pattern) so we don't send a request
  // on every keystroke.
  const handleBlur = () => {
    const nodeId = id
    const persist = data.onNodeDataChange
    setTimeout(() => {
      const current = nodeDataRef.current
      if (!current) return
      updateWorkflowContext(current)
      if (persist) {
        persist(nodeId, { ...current, expanded })
      }
    }, 0)
  }

  const toggleMinimize = () => {
    if (data.onNodeStateChange) {
      data.onNodeStateChange(id, !expanded)
    }
  }

  const handleDelete = async () => {
    if (!confirm("Are you sure you want to delete this node?")) {
      return
    }

    if (!currentWorkflow?.id || !stepDatabaseId) {
      console.error("Send SMS node: cannot delete -- missing workflow or step database id")
      return
    }

    router.delete(`/workflows/${currentWorkflow.id}/steps/${stepDatabaseId}`, {
      preserveState: true,
      preserveScroll: true,
      only: [],
      onSuccess: () => {
        if (typeof data.onDeleteNode === "function") {
          data.onDeleteNode(id)
        }
      },
      onError: (errors) => {
        console.error("Send SMS node: failed to delete step via Inertia", errors)
        alert("Failed to delete node. Please try again.")
      },
    })
  }

  // ---------- Media URL helpers ----------
  const addMediaUrl = () => {
    const updated = {
      ...nodeDataRef.current,
      mediaUrls: [...(nodeDataRef.current.mediaUrls || []), ""],
    }
    setNodeData(updated)
    persistData(updated)
  }

  const updateMediaUrl = (index, value) => {
    const mediaUrls = [...(nodeDataRef.current.mediaUrls || [])]
    mediaUrls[index] = ensureString(value)
    const updated = { ...nodeDataRef.current, mediaUrls }
    setNodeData(updated)
  }

  const removeMediaUrl = (index) => {
    const updated = {
      ...nodeDataRef.current,
      mediaUrls: (nodeDataRef.current.mediaUrls || []).filter((_, i) => i !== index),
    }
    setNodeData(updated)
    persistData(updated)
  }

  // ---------- Scroll capture (matches CreateNoteNode pattern) ----------
  useEffect(() => {
    const handleWheel = (e) => {
      const nodeContent = e.target.closest(".node-content")
      if (!nodeContent) return
      if (!e.target.closest(".text-node-outline")) return
      if (e.ctrlKey || e.shiftKey) return
      e.stopPropagation()
    }
    document.addEventListener("wheel", handleWheel, { passive: false, capture: true })
    return () => {
      document.removeEventListener("wheel", handleWheel, { capture: true })
    }
  }, [id, expanded])

  const inputClass =
    "w-full px-2 py-1.5 rounded border border-gray-300 text-sm bg-white text-gray-900 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 dark:focus:outline-none dark:focus:ring-2 dark:focus:ring-pink-500 nodrag"

  const templateMissing = nodeData.templateId && !selectedTemplate
  const templateInactive = selectedTemplate && !selectedTemplate.is_active

  const messagePreviewValue = nodeData.useTemplateMessage
    ? renderedMessage
    : nodeData.messageOverride || renderedMessage

  return (
    <div
      className={`trigger-node-container text-node-outline dark:bg-card ${!expanded ? "minimized" : ""} ${isHovering ? "text-node-hovering" : ""}`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="trigger-node-header dark:bg-card dark:border-border">
        <div className="trigger-node-title">
          <MessageSquare size={16} className="trigger-icon" />
          <span>
            {nodeData.name && nodeData.name.trim() !== "" ? nodeData.name : "Send SMS"}
          </span>
        </div>
        <div className="trigger-node-controls">
          <CopyPasteButtons
            nodeId={id}
            nodeType="sendTextNode"
            nodeName={nodeData.name}
            className="nodrag"
          />
          <div className="minimize-button-container nodrag">
            <button
              className="trigger-node-button nodrag dark:hover:bg-card dark:hover:text-white"
              onClick={(e) => {
                e.stopPropagation()
                toggleMinimize()
              }}
              onMouseEnter={() => setShowMinimizeTooltip(true)}
              onMouseLeave={() => setShowMinimizeTooltip(false)}
            >
              {!expanded ? <Plus size={16} /> : <Minus size={16} />}
            </button>
            {showMinimizeTooltip && <div className="minimize-tooltip"></div>}
          </div>
          <button
            className="trigger-node-button nodrag dark:hover:bg-card"
            onClick={(e) => {
              e.stopPropagation()
              handleDelete()
            }}
            title="Delete node"
            style={{ color: "#ef4444" }}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {expanded && (
        <div
          className="node-content nowheel dark:bg-card dark:text-white dark:border-border"
          onWheel={(e) => e.stopPropagation()}
        >
          {!twilio?.connected && (
            <div className="text-node-warning">
              <AlertTriangle size={12} />
              <span>Twilio is not connected. Configure it in Admin &rarr; Integrations.</span>
            </div>
          )}
          {(templateMissing || templateInactive) && (
            <div className="text-node-warning">
              <AlertTriangle size={12} />
              <span>
                {templateMissing
                  ? "Selected template is missing or unavailable."
                  : "Selected template is inactive."}
              </span>
            </div>
          )}

          <div className="node-field">
            <label className="dark:text-white">Node Name</label>
            <EnhancedVariableReference
              value={nodeData.name}
              onChange={(value) => handleChange("name", value)}
              onBlur={handleBlur}
              availableVariables={upstreamVariables}
              systemVariables={systemVariables}
              placeholder="Send SMS"
              className={inputClass}
              showDateVariables={true}
            />
          </div>

          <div className="node-field">
            <label className="dark:text-white">Description</label>
            <EnhancedVariableReference
              value={nodeData.description}
              onChange={(value) => handleChange("description", value)}
              onBlur={handleBlur}
              availableVariables={upstreamVariables}
              systemVariables={systemVariables}
              placeholder="Send SMS via Twilio"
              className={inputClass}
              showDateVariables={true}
            />
          </div>

          <div className="node-field">
            <label className="dark:text-white">
              Template<span className="text-node-required">*</span>
            </label>
            <select
              value={nodeData.templateId}
              onChange={(e) => handleTemplateChange(e.target.value)}
              onBlur={handleBlur}
              className={inputClass}
            >
              <option value="">Select a template...</option>
              {textTemplates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                  {template.category
                    ? ` (${TEXT_TEMPLATE_CATEGORIES[template.category] || template.category})`
                    : ""}
                </option>
              ))}
            </select>
            <div className="text-node-field-hint">
              Templates are managed under <strong>Admin &rarr; Text Templates</strong>.
            </div>
          </div>

          {/* Recipient Source toggle (variable vs static number) hidden for now.
              Uncomment to re-enable both options. Recipient defaults to "variable".
          <div className="node-field">
            <label className="dark:text-white">Recipient Source</label>
            <div className="text-node-radio-row">
              <label>
                <input
                  type="radio"
                  name={`text-node-recipient-source-${id}`}
                  value="variable"
                  checked={nodeData.recipientSource === "variable"}
                  onChange={() =>
                    handleChange("recipientSource", "variable", { persistOnChange: true })
                  }
                />
                From variable
              </label>
              <label>
                <input
                  type="radio"
                  name={`text-node-recipient-source-${id}`}
                  value="static"
                  checked={nodeData.recipientSource === "static"}
                  onChange={() =>
                    handleChange("recipientSource", "static", { persistOnChange: true })
                  }
                />
                Static number
              </label>
            </div>
          </div>
          */}

          <div className="node-field">
            <label className="dark:text-white">
              Recipient Phone
              <span className="text-node-required">*</span>
            </label>
            <EnhancedVariableReference
              value={nodeData.recipientPhone}
              onChange={(value) => handleChange("recipientPhone", value)}
              onBlur={handleBlur}
              availableVariables={upstreamVariables}
              systemVariables={systemVariables}
              placeholder="{{customer_phone}}"
              className={inputClass}
              showDateVariables={true}
            />
            <div className="text-node-field-hint">{VAR_HINT}</div>
          </div>

          {templateVariables.length > 0 && (
            <div className="node-field">
              <label className="dark:text-white">Variable Mapping</label>
              <div className="text-node-mapping-table">
                {templateVariables.map((variable) => {
                  const key = variable.key
                  return (
                    <div key={key} className="text-node-mapping-row">
                      <code className="text-node-mapping-slot">{`{{ ${key} }}`}</code>
                      <EnhancedVariableReference
                        value={nodeData.variableMappings?.[key] || ""}
                        onChange={(value) => handleMappingChange(key, value)}
                        onBlur={handleBlur}
                        availableVariables={upstreamVariables}
                        systemVariables={systemVariables}
                        placeholder={`Map ${key}`}
                        className={inputClass}
                        showDateVariables={true}
                      />
                    </div>
                  )
                })}
              </div>
              <div className="text-node-field-hint">
                Map template slots to upstream variables or literal values.
              </div>
            </div>
          )}

          <div className="node-field text-node-checkbox-row">
            <input
              id={`text-node-use-template-${id}`}
              type="checkbox"
              checked={Boolean(nodeData.useTemplateMessage)}
              onChange={(e) =>
                handleChange("useTemplateMessage", e.target.checked, { persistOnChange: true })
              }
              className="nodrag"
            />
            <label
              htmlFor={`text-node-use-template-${id}`}
              className="dark:text-white"
              style={{ marginBottom: 0, cursor: "pointer" }}
            >
              Use template message (uncheck to override)
            </label>
          </div>

          <div className="node-field">
            <label className="dark:text-white">Message Preview / Override</label>
            <textarea
              rows={5}
              value={messagePreviewValue}
              onChange={(e) => {
                const updated = {
                  ...nodeDataRef.current,
                  useTemplateMessage: false,
                  messageOverride: e.target.value,
                }
                setNodeData(updated)
              }}
              onBlur={handleBlur}
              disabled={nodeData.useTemplateMessage}
              placeholder={
                nodeData.useTemplateMessage
                  ? "Select a template to preview the message"
                  : "Override the template message"
              }
              className={`${inputClass} text-node-message-textarea`}
            />
            <div className="text-node-meta">
              {messagePreviewValue.length} character{messagePreviewValue.length !== 1 ? "s" : ""} ·{" "}
              {segmentCount} segment{segmentCount !== 1 ? "s" : ""}
            </div>
          </div>

          {/* MMS Media URLs section hidden for now (SMS only).
              Uncomment to re-enable adding image/media URLs to outgoing messages.
          <div className="node-field">
            <div className="text-node-media-header">
              <label className="dark:text-white" style={{ marginBottom: 0 }}>
                MMS Media URLs
              </label>
              <button
                type="button"
                className="text-node-link-btn nodrag"
                onClick={addMediaUrl}
              >
                + Add URL
              </button>
            </div>
            <select
              value={nodeData.mediaSource}
              onChange={(e) =>
                handleChange("mediaSource", e.target.value, { persistOnChange: true })
              }
              className={`${inputClass} text-node-media-source`}
            >
              <option value="both">Template + Node URLs</option>
              <option value="template">Template URLs only</option>
              <option value="node">Node URLs only</option>
            </select>
            {(nodeData.mediaUrls || []).length === 0 ? (
              <div className="text-node-field-hint">
                No media URLs configured. Recipients will receive an SMS only.
              </div>
            ) : (
              (nodeData.mediaUrls || []).map((url, index) => (
                <div key={index} className="text-node-media-row">
                  <EnhancedVariableReference
                    value={url}
                    onChange={(value) => updateMediaUrl(index, value)}
                    onBlur={handleBlur}
                    availableVariables={upstreamVariables}
                    systemVariables={systemVariables}
                    placeholder="https://example.com/image.jpg"
                    className={inputClass}
                    showDateVariables={true}
                  />
                  <button
                    type="button"
                    className="text-node-media-remove nodrag"
                    onClick={() => removeMediaUrl(index)}
                    title="Remove URL"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))
            )}
          </div>
          */}
        </div>
      )}

      <Handle type="target" position={Position.Top} id="b" />
      <Handle type="source" position={Position.Bottom} id="a" />
    </div>
  )
}

export default TextNode
